<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { getI18n } from '@/plugins/i18n'
import { usePlanningStore } from '@/stores/planning.store'
import { useNotify } from '@/composables/notify'
import { useDetectDevice } from '@/composables/device'
import { useRoiComparisonFormat } from '@/composables/roi-comparison'

import RoiComparisonEdit from '@/components/roi/RoiComparisonEdit.vue'

const props = defineProps({
  basket: {
    type: Object,
    required: true,
  },
})

const i18n = getI18n()
const router = useRouter()
const { roiComparisonEditStepEnum: STEP } = usePlanningStore()
const { notifySuccess } = useNotify()
const { isMobile } = useDetectDevice()
const { itemName } = useRoiComparisonFormat()

const showEdit = ref(false)

const onSaved = async(saved) => {
  showEdit.value = false
  props.basket.clear()
  notifySuccess(i18n.t('info.createdSuccessfully'))
  await router.push({ name: 'roi-comparison', query: { id: saved.id } })
}
</script>

<template>
  <div v-if="basket.items.length" class="roi-basket row items-center q-gutter-sm q-px-sm q-py-xs q-mb-sm">
    <q-icon name="mdi-chart-line" color="primary" size="sm" />
    <span class="text-weight-medium">{{ i18n.t('roiCompare.itemsToCompare') }}</span>
    <q-badge :color="basket.limitColor" :label="basket.limitLabel" />
    <q-chip
      v-for="item in basket.items"
      :key="item.id"
      dense
      removable
      @remove="basket.remove(item.id)"
    >
      {{ itemName(basket.type, item) }}
    </q-chip>
    <span v-if="!basket.isComparable" class="text-caption text-grey-7">{{ i18n.t('roiCompare.addAnother') }}</span>
    <q-space />
    <q-btn
      flat
      no-caps
      color="primary"
      :label="i18n.t('roiCompare.emptyBasket')"
      @click="basket.clear()"
    />
    <q-btn
      v-if="basket.isComparable"
      unelevated
      no-caps
      color="primary"
      :label="i18n.t('roiCompare.compareAction')"
      @click="showEdit = true"
    />

    <q-dialog v-model="showEdit" :maximized="isMobile" class="dialog-xs">
      <roi-comparison-edit
        v-if="showEdit"
        :model-value="{ data: { type: basket.type } }"
        :items="basket.items"
        :initial-step="STEP.REFERENCE"
        @update="onSaved"
        @cancel="showEdit = false"
      />
    </q-dialog>
  </div>
</template>

<style scoped>
.roi-basket {
  background-color: color-mix(in srgb, var(--q-primary) 8%, transparent);
  border: 1px solid color-mix(in srgb, var(--q-primary) 25%, transparent);
  border-radius: .25rem;
}
</style>
